import React from "react";
import { useParams } from "react-router-dom";

/**
 * HOC that forces component remount when a route param changes.
 * Adds a key prop based on the param value to trigger React remounting.
 *
 * @param Component The component to wrap.
 * @param paramName The route param to use as key.
 * @returns A component that remounts when the param changes.
 *
 * @example
 * const ProductDetailsWithKey = withRouteKey(ProductDetails, "coreId");
 * <Route path="product/:coreId" element={<ProductDetailsWithKey />} />
 */
export function withRouteKey<P extends object>(
  Component: React.ComponentType<P>,
  paramName: string,
) {
  const WrappedComponent = (props: P) => {
    const params = useParams();
    return <Component {...props} key={params[paramName]} />;
  };

  const componentName = Component.displayName || Component.name || "Component";
  WrappedComponent.displayName = `withRouteKey(${componentName})`;

  return WrappedComponent;
}
